import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import { Link } from 'react-router-dom';
import Portal from '../../_common/Portal';
import {
  StyledTrackerItem,
  TrackerBullet,
  TrackerContentWrapper,
  TrackerContent,
  TrackerLine,
  TrackerContentLine,
  TrackerAnchor,
} from '../styles';

export type Item = {
  label: ReactNode;
  id: string;
  isComplete?: boolean;
  isDisabled?: boolean;
  href?: string;
  content?: ReactNode;
  alwaysShowContent?: boolean;
};

export interface TrackerItemProps {
  label: ReactNode;
  id: string;
  isComplete: boolean;
  isDisabled: boolean;
  active: string;
  href: string;
  content?: ReactNode;
  isFirst: boolean;
  isLast: boolean;
  isHorizontal: boolean;
  horizontalContentContainer?: HTMLElement | null;
  withReactRouter: boolean;
  onClick: (item: Item, index: number, Event: any) => void;
  alwaysShowContent?: boolean;
}

const getItemTag = (href: string, withReactRouter: boolean, onClick) => {
  if (withReactRouter) {
    return props => <TrackerAnchor as={Link} to={href} onClick={onClick} {...props} />;
  }

  if (href) {
    return props => <TrackerAnchor href={href} onClick={onClick} {...props} />;
  }

  return props => <TrackerAnchor as="button" onClick={onClick} {...props} />;
};

/**
 * @purpose Allows users to insert long strings of data
 */
function TrackerItem(props: TrackerItemProps) {
  const {
    label,
    id,
    href,
    content,
    isComplete,
    isDisabled,
    active,
    isFirst,
    isLast,
    isHorizontal,
    horizontalContentContainer,
    onClick,
    withReactRouter,
    alwaysShowContent,
  } = props;

  function isActive(): boolean {
    return active === id;
  }

  // the alwaysShowContent prop doesn't apply to horizontal Tracker items
  const isActiveHorizontalItem = isActive() && isHorizontal;
  const isActiveVerticalItem = (alwaysShowContent || isActive()) && !isHorizontal;

  const ItemTag = getItemTag(href, withReactRouter, onClick);

  let contentInner;
  if (content) {
    contentInner = (
      <TrackerContent>
        <TrackerContentLine />
        <TrackerContentWrapper>{content}</TrackerContentWrapper>
      </TrackerContent>
    );
  }

  let contentComponent;
  if (isActiveHorizontalItem) {
    contentComponent = <Portal children={contentInner} container={horizontalContentContainer || undefined} />;
  } else if (isActiveVerticalItem) {
    contentComponent = contentInner;
  }

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        trackerItemActive: isActive(),
        trackerItemDisabled: isDisabled,
        trackerItemCompleted: isComplete,
        trackerHorizontal: isHorizontal,
        trackerContentActive: isActiveVerticalItem || isActiveHorizontalItem,
        trackerItemFirst: isFirst,
        trackerItemLast: isLast,
      })}
    >
      <StyledTrackerItem>
        <ItemTag
          aria-current={isActive() ? 'step' : undefined}
          aria-disabled={isDisabled ? 'true' : undefined}
          data-first={isFirst ? 'true' : undefined}
          data-last={isLast ? 'true' : undefined}
          tabIndex={isDisabled ? -1 : undefined}
        >
          <TrackerBullet />
          <TrackerLine />
          {label}
        </ItemTag>
        {contentComponent}
      </StyledTrackerItem>
    </ThemeProvider>
  );
}

TrackerItem.defaultProps = {
  href: undefined,
  content: undefined,
  isFirst: false,
  isLast: false,
  isHorizontal: false,
  horizontalContentContainer: undefined,
  withReactRouter: false,
  isComplete: false,
  isDisabled: false,
  onClick: () => {},
};

TrackerItem.displayName = 'Tracker.Item';

export default TrackerItem;
